import { useState, useMemo, useCallback } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table'
import { useBusinesses, useUsers } from '@/hooks/useAdminData'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { CreateUserForm } from '@/components/users/CreateUserForm'
import { UserDetailPanel } from '@/components/users/UserDetailPanel'
import { UserActionsMenu } from '@/components/users/UserActionsMenu'
import { ResetPasswordDialog } from '@/components/users/ResetPasswordDialog'
import { adminDisableUser, adminDeleteUser } from '@/lib/admin-functions'
import { initialsOf } from '@/lib/normalize'
import { enumLabel } from '@/lib/enumLabel'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import type { User } from '@/types'
import { Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react'

const columnHelper = createColumnHelper<User>()

export function UsersPage() {
  const { t } = useTranslation()
  const [showCreate, setShowCreate] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [actionLoading, setActionLoading] = useState(false)
  const [resetTarget, setResetTarget] = useState<User | null>(null)

  const { userProfile } = useAuth()
  const { data: users, isLoading, refetch } = useUsers()
  const { data: businesses } = useBusinesses()

  const businessMap = useMemo(() => {
    const map: Record<string, string> = {}
    businesses?.forEach((b) => {
      map[b.id] = b.name
    })
    return map
  }, [businesses])

  const filteredData = useMemo(() => {
    let data = users ?? []
    if (search) {
      const lower = search.toLowerCase()
      data = data.filter(
        (u) =>
          u.email.toLowerCase().includes(lower) ||
          u.displayName.toLowerCase().includes(lower)
      )
    }
    if (roleFilter) {
      data = data.filter((u) => u.role === roleFilter)
    }
    return data
  }, [users, search, roleFilter])

  const handleToggleActive = useCallback(async (user: User) => {
    setActionLoading(true)
    try {
      await adminDisableUser({ uid: user.id, disabled: user.isActive })
      refetch()
    } catch {
      alert(t('users.alerts.statusFailed'))
    } finally {
      setActionLoading(false)
    }
  }, [refetch, t])

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'avatar',
        cell: ({ row }) => (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
            {initialsOf(row.original.displayName)}
          </div>
        ),
        size: 50,
      }),
      columnHelper.accessor('displayName', {
        header: () => t('users.columns.name'),
        cell: ({ row }) => (
          <button
            onClick={() => setSelectedUser(row.original)}
            className="text-left hover:text-primary-600"
          >
            <p className="text-sm font-medium">{row.original.displayName}</p>
            <p className="text-xs text-gray-500">{row.original.email}</p>
          </button>
        ),
      }),
      columnHelper.accessor('role', {
        header: () => t('users.columns.role'),
        cell: ({ getValue }) => {
          const role = getValue()
          const variant = role === 'admin' ? 'info' : role === 'caregiver' ? 'success' : 'default'
          return <Badge variant={variant}>{enumLabel(t, 'roles', role)}</Badge>
        },
      }),
      columnHelper.accessor('businessId', {
        header: () => t('users.columns.business'),
        cell: ({ getValue }) => (
          <span className="text-sm text-gray-600">{businessMap[getValue()] ?? getValue()}</span>
        ),
      }),
      columnHelper.accessor('isActive', {
        header: () => t('users.columns.status'),
        cell: ({ getValue }) => (
          <Badge variant={getValue() ? 'success' : 'danger'}>
            {getValue() ? t('common.active') : t('common.disabled')}
          </Badge>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row }) => (
          <UserActionsMenu
            user={row.original}
            busy={actionLoading}
            isSelf={row.original.id === userProfile?.id}
            onViewDetails={setSelectedUser}
            onResetPassword={setResetTarget}
            onToggleActive={handleToggleActive}
            onDelete={setDeleteTarget}
          />
        ),
        size: 50,
      }),
    ],
    [businessMap, actionLoading, handleToggleActive, userProfile?.id, t]
  )

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 20 } },
  })

  const handleDeleteUser = async () => {
    if (!deleteTarget) return
    setActionLoading(true)
    try {
      await adminDeleteUser({ uid: deleteTarget.id })
      refetch()
      setDeleteTarget(null)
    } catch (error) {
      console.error('Delete user error:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      alert(t('users.alerts.deleteFailed', { error: errorMessage }))
    } finally {
      setActionLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('users.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:w-64"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">{t('users.allRoles')}</option>
            <option value="care_recipient">{t('roles.care_recipient')}</option>
            <option value="caregiver">{t('roles.caregiver')}</option>
            <option value="admin">{t('roles.admin')}</option>
          </select>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" />
          {t('users.add')}
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b border-gray-200">
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-6"
                      style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-100">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 sm:px-6">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 sm:px-6">
          <p className="text-sm text-gray-500">
            {t('users.count', { count: filteredData.length })}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label={t('common.previousPage')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600">
              {t('common.pageOf', {
                page: table.getState().pagination.pageIndex + 1,
                total: table.getPageCount(),
              })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label={t('common.nextPage')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={t('users.createTitle')} className="max-w-lg">
        <CreateUserForm
          businesses={businesses ?? []}
          onSuccess={() => {
            setShowCreate(false)
            refetch()
          }}
          onCancel={() => setShowCreate(false)}
        />
      </Modal>

      <Modal
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        title={t('users.detailsTitle')}
        className="max-w-lg"
      >
        {selectedUser && (
          <UserDetailPanel
            user={selectedUser}
            businessName={businessMap[selectedUser.businessId]}
            onClose={() => setSelectedUser(null)}
          />
        )}
      </Modal>

      <ResetPasswordDialog user={resetTarget} onClose={() => setResetTarget(null)} />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteUser}
        title={t('users.deleteTitle')}
        message={t(
          deleteTarget?.role === 'care_recipient' ? 'users.deleteMessageRecipient' : 'users.deleteMessageMember',
          { name: deleteTarget?.displayName ?? '' }
        )}
        confirmLabel={t('users.actions.delete')}
        loading={actionLoading}
      />
    </div>
  )
}
